import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { TabTitleService } from "../shared/services/tab-title.service";
import { SsoAuthService } from "../shared/services/sso-auth.service";
import { Subscription } from "rxjs";
import { DashboardConfig } from "../shared/model/dashboard-config.model";
import { DashboardService } from "../shared/services/dashboard.service";
import { DateTime } from "luxon";
import { filter } from "rxjs/operators";

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DashboardComponent implements OnInit {

  private ssoAuthSub: Subscription;
  private userRoleId: string = '';
  public userFirstName: string = '';

  public selectedPhaseIndex: number = 0;

  public dashboardConfig: DashboardConfig = null;

  nowISO: string = DateTime.fromJSDate(new Date()).setZone("UTC").toISO();

  constructor(
    private tabTitleService: TabTitleService,
    private ssoAuthService: SsoAuthService,
    private dashboardService: DashboardService
  ) { }

  ngOnInit() {
    this.tabTitleService.setTabTitle('Dashboard');
    this.ssoAuthSub = this.ssoAuthService.currentRpUser$
    .pipe(
      filter(rpUser => !!rpUser)
    )
    .subscribe(async rpUser => {
      this.userRoleId = rpUser.currentRole.id;
      this.userFirstName = rpUser.firstName;

      // getting dashboard config for the current role of the user
      const resp: any = await this.dashboardService.getDashboardConfig(this.userRoleId).toPromise();
      // try catching if the config value coming from RP is JSON parsable
      try {
        this.dashboardConfig = JSON.parse(resp.config.value);
      } catch (e) {
        this.dashboardConfig = {
          dashboardSettings: '',
          phases: [],
        }
      };

      if (this.dashboardConfig.phases.length > 0) {
        this.dashboardConfig.phases.forEach((phase, index) => {
          // if we have both start and end date for a phase, we display a progress bar for it
          if (phase.phaseStartDate && phase.phaseEndDate) {
            const luxonPhaseStartDate = DateTime.fromISO(phase.phaseStartDate);
            const luxonPhaseEndDate = DateTime.fromISO(phase.phaseEndDate);
            // difference is negative number
            const phaseDateDiffInSeconds = luxonPhaseStartDate.diff(luxonPhaseEndDate, 'seconds').toObject().seconds;
            // difference is negative number if phase end date has not occured yet, otherwise it's positive
            const nowDiffToPhaseEndDateInSeconds = DateTime.fromJSDate(new Date()).setZone("UTC").diff(luxonPhaseEndDate, 'seconds').toObject().seconds;

            if (nowDiffToPhaseEndDateInSeconds > 0) {
              // phase ended
              phase.phaseProgress = 100;
            } else if (nowDiffToPhaseEndDateInSeconds < phaseDateDiffInSeconds) {
              // phase hasn't started
              phase.phaseProgress = 0;
            } else {
              // phase is in progress
              // (1) phase progress in percentage is:
              // (2) the difference between the current time and the phase end date
              // (3) divided by the total difference between phase start and end date 
              // (4) times 100 to get percentage difference between them
              // (5) however, since the now difference is declinining (because it's a negative number)
              // (6) we substract the given percentage from 100 to get the real percentage
              let phaseProgressInPercentage = 100 - ((nowDiffToPhaseEndDateInSeconds / phaseDateDiffInSeconds) * 100);
              phase.phaseProgress = Math.round(phaseProgressInPercentage * 1e2 ) / 1e2;
            }
          } else {
            // if we don't have start and end dates, the phase progress is null
            phase.phaseProgress = null;
          }

          // increase active phase index if current date has passed any phase end date
          if (index !== (this.dashboardConfig.phases.length - 1)) {
            if (phase.phaseEndDate < this.nowISO) {
              this.selectedPhaseIndex++;
            }
          }
        });
      };
    });
  }

  navigateToPrevPhase() {
    this.selectedPhaseIndex = this.selectedPhaseIndex === 0 ? this.selectedPhaseIndex : this.selectedPhaseIndex - 1;
  }

  navigateToNextPhase() {
    this.selectedPhaseIndex = this.selectedPhaseIndex === this.dashboardConfig.phases.length - 1 ? this.selectedPhaseIndex : this.selectedPhaseIndex + 1;
  }

  ngOnDestroy() {
    this.ssoAuthSub.unsubscribe();
  }
}
